import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';

function BadgeCollection({ userId, profile, onClose, guestMode }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecords();
  }, [userId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      if (guestMode) {
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        setRecords(guestRecords);
      } else {
        const recordsRef = collection(db, 'users', userId, 'records');
        const q = query(recordsRef, orderBy('date', 'asc'));
        const querySnapshot = await getDocs(q);

        const recordsData = [];
        querySnapshot.forEach((doc) => {
          recordsData.push({ id: doc.id, ...doc.data() });
        });

        setRecords(recordsData);
      }
    } catch (error) {
      console.error('기록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 뱃지 정의
  const badgeDefinitions = [
    // 첫 기록 뱃지
    {
      id: 'first_record',
      name: '첫 걸음',
      emoji: '🎯',
      description: '첫 성장 기록 등록',
      color: '#E8A87C',
      category: 'record'
    },
    {
      id: 'record_5',
      name: '꾸준함',
      emoji: '📝',
      description: '5개 기록 달성',
      color: '#81C784',
      category: 'record'
    },
    {
      id: 'record_10',
      name: '기록왕',
      emoji: '📚',
      description: '10개 기록 달성',
      color: '#66BB6A',
      category: 'record'
    },

    // 키 마일스톤
    {
      id: 'height_50',
      name: '50cm 돌파',
      emoji: '📏',
      description: '키 50cm 달성',
      color: '#64B5F6',
      category: 'height'
    },
    {
      id: 'height_60',
      name: '60cm 돌파',
      emoji: '📐',
      description: '키 60cm 달성',
      color: '#42A5F5',
      category: 'height'
    },
    {
      id: 'height_70',
      name: '70cm 돌파',
      emoji: '📊',
      description: '키 70cm 달성',
      color: '#2196F3',
      category: 'height'
    },
    {
      id: 'height_80',
      name: '80cm 돌파',
      emoji: '🎯',
      description: '키 80cm 달성',
      color: '#1976D2',
      category: 'height'
    },
    {
      id: 'height_90',
      name: '90cm 돌파',
      emoji: '🏆',
      description: '키 90cm 달성',
      color: '#1565C0',
      category: 'height'
    },

    // 몸무게 마일스톤
    {
      id: 'weight_5',
      name: '5kg 돌파',
      emoji: '⚖️',
      description: '몸무게 5kg 달성',
      color: '#FFB74D',
      category: 'weight'
    },
    {
      id: 'weight_7',
      name: '7kg 돌파',
      emoji: '💪',
      description: '몸무게 7kg 달성',
      color: '#FFA726',
      category: 'weight'
    },
    {
      id: 'weight_10',
      name: '10kg 돌파',
      emoji: '🎖️',
      description: '몸무게 10kg 달성',
      color: '#FF9800',
      category: 'weight'
    },
    {
      id: 'weight_12',
      name: '12kg 돌파',
      emoji: '🏅',
      description: '몸무게 12kg 달성',
      color: '#FB8C00',
      category: 'weight'
    },
    {
      id: 'weight_15',
      name: '15kg 돌파',
      emoji: '👑',
      description: '몸무게 15kg 달성',
      color: '#F57C00',
      category: 'weight'
    },

    // 성장 속도 뱃지
    {
      id: 'fast_grower',
      name: '고속 성장',
      emoji: '🚀',
      description: '한 달에 3cm 이상 성장',
      color: '#66BB6A',
      category: 'speed'
    },
    {
      id: 'steady_grower',
      name: '꾸준한 성장',
      emoji: '✨',
      description: '매달 꾸준히 성장 중',
      color: '#A8D5BA',
      category: 'speed'
    },

    // 백분위 뱃지
    {
      id: 'top_height',
      name: '키 상위권',
      emoji: '🌟',
      description: '키 75 백분위 이상',
      color: '#BA68C8',
      category: 'percentile'
    },
    {
      id: 'top_weight',
      name: '몸무게 상위권',
      emoji: '⭐',
      description: '몸무게 75 백분위 이상',
      color: '#AB47BC',
      category: 'percentile'
    },

    // 특별 뱃지
    {
      id: 'healthy_baby',
      name: '건강한 아기',
      emoji: '💖',
      description: '모든 수치 정상 범위',
      color: '#E57373',
      category: 'special'
    }
  ];

  // 뱃지 획득 여부 체크
  const checkBadge = (badge) => {
    if (records.length === 0) return false;

    const latestRecord = records[records.length - 1];

    switch (badge.id) {
      case 'first_record':
        return records.length >= 1;
      case 'record_5':
        return records.length >= 5;
      case 'record_10':
        return records.length >= 10;

      case 'height_50':
        return latestRecord.height >= 50;
      case 'height_60':
        return latestRecord.height >= 60;
      case 'height_70':
        return latestRecord.height >= 70;
      case 'height_80':
        return latestRecord.height >= 80;
      case 'height_90':
        return latestRecord.height >= 90;

      case 'weight_5':
        return latestRecord.weight >= 5;
      case 'weight_7':
        return latestRecord.weight >= 7;
      case 'weight_10':
        return latestRecord.weight >= 10;
      case 'weight_12':
        return latestRecord.weight >= 12;
      case 'weight_15':
        return latestRecord.weight >= 15;

      case 'fast_grower':
        if (records.length < 2) return false;
        const prevRecord = records[records.length - 2];
        const daysDiff = (new Date(latestRecord.date) - new Date(prevRecord.date)) / (1000 * 60 * 60 * 24);
        const monthsDiff = daysDiff / 30.44;
        const heightGrowth = latestRecord.height - prevRecord.height;
        return monthsDiff >= 0.5 && heightGrowth / monthsDiff >= 3;

      case 'steady_grower':
        return records.length >= 3 && records.every((r, i) => {
          if (i === 0) return true;
          return r.height > records[i - 1].height;
        });

      case 'top_height':
      case 'top_weight':
      case 'healthy_baby':
        // 백분위 계산이 필요한 뱃지는 추후 구현
        return false;

      default:
        return false;
    }
  };

  const earnedBadges = badgeDefinitions.filter(badge => checkBadge(badge));
  const lockedBadges = badgeDefinitions.filter(badge => !checkBadge(badge));

  const getCategoryName = (category) => {
    switch (category) {
      case 'record': return '기록 관리';
      case 'height': return '키 성장';
      case 'weight': return '몸무게 성장';
      case 'speed': return '성장 속도';
      case 'percentile': return '백분위 달성';
      case 'special': return '특별 업적';
      default: return '기타';
    }
  };

  const categories = [...new Set(badgeDefinitions.map(b => b.category))];

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355'
          }}
        >
          ×
        </button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
          <h2 style={{ color: '#E8A87C', fontSize: '24px', marginBottom: '8px' }}>
            성장 뱃지 컬렉션
          </h2>
          <p style={{ color: '#8B7355', fontSize: '14px' }}>
            {profile.name}의 성장 업적
          </p>
          <div style={{
            backgroundColor: '#FAF3E8',
            borderRadius: '12px',
            padding: '12px',
            marginTop: '16px',
            display: 'inline-block'
          }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#E8A87C' }}>
              {earnedBadges.length}
            </span>
            <span style={{ color: '#8B7355', fontSize: '14px' }}>
              {' '}/ {badgeDefinitions.length} 획득
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#8B7355' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>불러오는 중...</p>
          </div>
        ) : (
          <>
            {/* 획득한 뱃지 */}
            {earnedBadges.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  color: '#E8A87C',
                  fontSize: '18px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ✨ 획득한 뱃지
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '12px'
                }}>
                  {earnedBadges.map(badge => (
                    <div
                      key={badge.id}
                      className="badge-earned"
                      style={{
                        backgroundColor: badge.color,
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <div style={{ fontSize: '36px', marginBottom: '8px' }}>{badge.emoji}</div>
                      <div style={{
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        {badge.name}
                      </div>
                      <div style={{
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '10px'
                      }}>
                        {badge.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 잠긴 뱃지 */}
            {lockedBadges.length > 0 && (
              <div>
                <h3 style={{
                  color: '#8B7355',
                  fontSize: '18px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🔒 획득 가능한 뱃지
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '12px'
                }}>
                  {lockedBadges.map(badge => (
                    <div
                      key={badge.id}
                      style={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        opacity: 0.6,
                        border: '2px dashed #E0E0E0'
                      }}
                    >
                      <div style={{ fontSize: '36px', marginBottom: '8px', filter: 'grayscale(1)' }}>
                        {badge.emoji}
                      </div>
                      <div style={{
                        color: '#9E9E9E',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        {badge.name}
                      </div>
                      <div style={{
                        color: '#BDBDBD',
                        fontSize: '10px'
                      }}>
                        {badge.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{
          backgroundColor: '#F5E6D3',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '24px',
          marginBottom: '20px'
        }}>
          <p style={{
            color: '#8B7355',
            fontSize: '12px',
            margin: 0,
            lineHeight: '1.6'
          }}>
            💡 <strong>뱃지 획득 팁</strong><br />
            • 꾸준히 기록하면 더 많은 뱃지를 획득할 수 있어요<br />
            • 건강하게 자라는 것이 가장 중요해요<br />
            • 획득한 뱃지는 SNS에 공유해보세요!
          </p>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

export default BadgeCollection;
